import {
  ADMIN_SALE_STATE_MAP,
  ADMIN_SETTLEMENT_STATUS_MAP,
  DerivedInvoiceDisplayState,
  INVOICE_PAYMENT_STATUS_LABELS,
  InvoicePaymentStatusSchema,
  PaymentMethodSchema,
  SettlementStatusSchema,
} from '@neon-one/auctions-types';
import type { InvoiceDisplayState, InvoicePaymentStatus, StatusDisplay, StatusSeverity } from '@neon-one/auctions-types';

import type { PaymentInvoice, PaymentStatus } from './types';

export type PaymentSeverity = StatusSeverity;
export type StatePill = StatusDisplay;

export const ALL_PAYMENT_STATUSES: readonly PaymentStatus[] = SettlementStatusSchema.options;
const STATUS = SettlementStatusSchema.enum;
const LEDGER_STATUS = InvoicePaymentStatusSchema.enum;

export const InvoiceStatus = {
  Paid: 'paid',
  AwaitingPayment: 'awaiting_payment',
  Declined: 'declined',
  Processing: 'processing',
  Clearing: STATUS.clearing,
  Unconfirmed: STATUS.unconfirmed,
  NothingDue: DerivedInvoiceDisplayState.NothingDue,
  RefundFailed: DerivedInvoiceDisplayState.RefundFailed,
  WaitingForTap: DerivedInvoiceDisplayState.WaitingForTap,
  Refunded: STATUS.refunded,
  PartiallyRefunded: STATUS.partially_refunded,
  Void: STATUS.void,
} as const;
export type InvoiceStatusValue = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

// §3.1 — Operational states remain separate from attention labels and qualifiers.
export const designStatus = (status: PaymentStatus): InvoiceStatusValue => {
  switch (status) {
    case STATUS.paid_auto:
    case STATUS.paid_online:
    case STATUS.paid_offline:
    case STATUS.clearing:
    case STATUS.unconfirmed:
      return InvoiceStatus.Paid;
    case STATUS.awaiting:
    case STATUS.no_card:
    case STATUS.failed:
      return InvoiceStatus.AwaitingPayment;
    default:
      return status;
  }
};

const DISPLAY_STATE = {
  [InvoiceStatus.Paid]: STATUS.paid_online,
  [InvoiceStatus.AwaitingPayment]: STATUS.awaiting,
  [InvoiceStatus.Declined]: STATUS.failed,
  [InvoiceStatus.Processing]: STATUS.processing,
  [InvoiceStatus.Clearing]: STATUS.clearing,
  [InvoiceStatus.Unconfirmed]: STATUS.unconfirmed,
  [InvoiceStatus.NothingDue]: DerivedInvoiceDisplayState.NothingDue,
  [InvoiceStatus.RefundFailed]: DerivedInvoiceDisplayState.RefundFailed,
  [InvoiceStatus.WaitingForTap]: DerivedInvoiceDisplayState.WaitingForTap,
  [InvoiceStatus.Refunded]: STATUS.refunded,
  [InvoiceStatus.PartiallyRefunded]: STATUS.partially_refunded,
  [InvoiceStatus.Void]: STATUS.void,
} as const satisfies Record<InvoiceStatusValue, InvoiceDisplayState>;

const PAID: readonly PaymentStatus[] = [STATUS.paid_auto, STATUS.paid_online, STATUS.paid_offline];
const NEEDS_WORK: readonly PaymentStatus[] = [STATUS.failed, STATUS.no_card, STATUS.awaiting];
const COLLECTED: readonly PaymentStatus[] = [
  ...PAID, STATUS.clearing, STATUS.unconfirmed, STATUS.partially_refunded, STATUS.refunded,
];
const RESOLVED: readonly PaymentStatus[] = [...COLLECTED, STATUS.processing, STATUS.void];

const NOT_A_DECLINE: ReadonlySet<string> = new Set(['terminal_canceled', 'terminal_timeout']);

export const isDeclineFailure = (failure: PaymentInvoice['lastFailure']): boolean =>
  failure != null && !NOT_A_DECLINE.has(failure.code ?? '');

const declineOnRecord = (invoice: InvoiceDisplayInput): boolean => {
  if (NOT_A_DECLINE.has(invoice.lastFailure?.code ?? '')) return false;
  return invoice.status === STATUS.failed || invoice.lastFailure != null;
};

type RefundAmounts = Partial<Pick<PaymentInvoice,
  'refundedCents' | 'pendingRefundCents' | 'refundSettledCents' | 'refundPendingCents' | 'owedBackCents'
>>;
export type InvoiceDisplayInput = Pick<PaymentInvoice, 'status'> & RefundAmounts
  & Partial<Pick<PaymentInvoice, 'totalCents' | 'lastFailure' | 'inFlightCharge'>>;

/** Submitted refunds and failed obligations no longer belong to collected money. */
export const refundsCommittedCents = (invoice: RefundAmounts): number =>
  (invoice.refundSettledCents ?? invoice.refundedCents ?? 0)
  + (invoice.refundPendingCents ?? invoice.pendingRefundCents ?? 0)
  + (invoice.owedBackCents ?? 0);

/** Same kept-money formula as eventMoney, with aliases for older settlement responses. */
export const netPaidCents = (invoice: Pick<PaymentInvoice, 'totalCents'> & RefundAmounts): number =>
  Math.max(0, invoice.totalCents - refundsCommittedCents(invoice));

/** Refund settling supplements the stored label instead of replacing Paid or Refunded. */
export const paymentQualifier = (invoice: InvoiceDisplayInput): string | undefined => {
  if (invoice.status === STATUS.void) return undefined;
  const display: StatePill = ADMIN_SETTLEMENT_STATUS_MAP[invoice.status];
  const settling = (invoice.refundPendingCents ?? invoice.pendingRefundCents ?? 0) > 0
    ? ADMIN_SETTLEMENT_STATUS_MAP[DerivedInvoiceDisplayState.RefundSettling].label : undefined;
  return [display.qualifier, settling].filter(Boolean).join(' · ') || undefined;
};

// §1.1 — Attention readings override labels, never operational chargeability.
export const invoiceDisplayStatus = (invoice: InvoiceDisplayInput): InvoiceStatusValue => {
  if (invoice.status === STATUS.void) return InvoiceStatus.Void;
  if ((invoice.owedBackCents ?? 0) > 0) return InvoiceStatus.RefundFailed;
  const design = designStatus(invoice.status);
  if (design === InvoiceStatus.AwaitingPayment) {
    if (invoice.totalCents !== undefined && netPaidCents({ ...invoice, totalCents: invoice.totalCents }) === 0) {
      return InvoiceStatus.NothingDue;
    }
    return declineOnRecord(invoice) ? InvoiceStatus.Declined : design;
  }
  if (invoice.status === STATUS.clearing) return InvoiceStatus.Clearing;
  if (invoice.status === STATUS.unconfirmed) return InvoiceStatus.Unconfirmed;
  if (invoice.status === STATUS.processing && invoice.inFlightCharge?.rail === PaymentMethodSchema.enum.terminal) {
    return InvoiceStatus.WaitingForTap;
  }
  return design;
};

export const invoiceStatusPill = (status: InvoiceStatusValue): StatePill =>
  ADMIN_SETTLEMENT_STATUS_MAP[DISPLAY_STATE[status]];

export const invoicePill = (invoice: InvoiceDisplayInput): StatePill => {
  const pill = invoiceStatusPill(invoiceDisplayStatus(invoice));
  const qualifier = paymentQualifier(invoice);
  return qualifier ? { ...pill, qualifier } : pill;
};

export const statusSeverity = (status: PaymentStatus): PaymentSeverity => ADMIN_SETTLEMENT_STATUS_MAP[status].severity;
export const statusLabel = (status: PaymentStatus): string => ADMIN_SETTLEMENT_STATUS_MAP[status].label;
export const isPaid = (status: PaymentStatus): boolean => PAID.includes(status);
export const moneyCollected = (status: PaymentStatus): boolean => COLLECTED.includes(status);
export const isResolved = (status: PaymentStatus): boolean => RESOLVED.includes(status);
export const tookAuthorization = (invoice: Pick<PaymentInvoice, 'status'>): boolean => moneyCollected(invoice.status);
export const chargeInFlight = (status: PaymentStatus): boolean =>
  status === STATUS.processing || status === STATUS.clearing || status === STATUS.unconfirmed;
export const releaseRefunds = (status: PaymentStatus): boolean => moneyCollected(status) || chargeInFlight(status);

export const liveTaxDeductibleCents = (
  inv: Pick<PaymentInvoice, 'lineItems'> | null,
): number => {
  if (!inv) return 0;
  return inv.lineItems
    .filter(
      (li) =>
        !li.declined
        && (li.refundedCents ?? 0) + (li.inFlightRefundCents ?? 0) < li.amountCents,
    )
    .reduce((sum, li) => sum + li.taxDeductibleCents, 0);
};

export const statusNeedsWork = (status: PaymentStatus): boolean => NEEDS_WORK.includes(status);
export const collectibleCents = netPaidCents;
export const needsWork = (invoice: Pick<PaymentInvoice, 'status' | 'totalCents'> & RefundAmounts): boolean =>
  statusNeedsWork(invoice.status) && collectibleCents(invoice) > 0;

const INVOICE_PAYMENT_DESIGN_STATUS = {
  [LEDGER_STATUS.pending]: InvoiceStatus.AwaitingPayment,
  [LEDGER_STATUS.processing]: InvoiceStatus.Processing,
  [LEDGER_STATUS.paid]: InvoiceStatus.Paid,
  [LEDGER_STATUS.failed]: InvoiceStatus.Declined,
  [LEDGER_STATUS.refunded]: InvoiceStatus.Refunded,
  [LEDGER_STATUS.partially_refunded]: InvoiceStatus.PartiallyRefunded,
  [LEDGER_STATUS.void]: InvoiceStatus.Void,
} as const satisfies Record<InvoicePaymentStatus, InvoiceStatusValue>;

export const invoicePaymentDesignStatus = (status: InvoicePaymentStatus): InvoiceStatusValue =>
  INVOICE_PAYMENT_DESIGN_STATUS[status];
export const invoicePaymentStatusLabel = (status: InvoicePaymentStatus): string =>
  INVOICE_PAYMENT_STATUS_LABELS[status];
export const isVoidPayment = (status: InvoicePaymentStatus): boolean => status === LEDGER_STATUS.void;

export const itemStatusPill = (status: PaymentStatus, saleCanceledAt: string | null): StatePill =>
  saleCanceledAt !== null ? ADMIN_SALE_STATE_MAP.sale_canceled : ADMIN_SETTLEMENT_STATUS_MAP[status];

/**
 * A declined card with money still to collect. The Declined tile, its owed figure and
 * the attention rows all count by this, so they always agree. It reads the same display
 * status the pill does, which covers a charge a supervisor wrote off with no failure
 * record; an invoice merely awaiting payment is the bidder's to act on and is not here.
 */
export const isDeclinedInvoice = (invoice: PaymentInvoice): boolean =>
  needsWork(invoice) && invoiceDisplayStatus(invoice) === InvoiceStatus.Declined;
